import { CloseOutlined } from "@ant-design/icons";
import { Modal, Tooltip } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";

import { ReactComponent as AddPatient } from "../../assets/svg/add-patient-icon.svg";
import { GRAY_300 } from "../../constant/colors";
import DiscardChangesPopup from "../sharedComponents/DiscardChangesPopup";
import DiscardListChangesModal from "../FilterList/DiscardListChangesModal";
import NewPatientForm from "./NewPatientForm";
import { PrimaryActionButton } from "@tailormed/common-client/component/AppShell";
import ACTIONS from "../../store/action";
import { selectFilterList, selectFilterListStatuses, selectUser } from "../../store/selector";
import { useGetSetting } from "../../hooks/getSetting";
import { roles } from "../../constant/roles";

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
  }
`;

const AddNewPatientButton = ({ collapsed }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const user = useSelector(selectUser);
  const [isManualPatientCreationEnabled] = useGetSetting(["patient.end-user-manual-creation.enabled"]);

  const [open, setOpen] = useState(false);
  const [discardChangesPopupVisible, setDiscardChangesPopupVisible] = useState(false);
  const [destroyPopoverOnClose, setDestroyPopoverOnClose] = useState(false);
  const [discardChangesFunc, setDiscardChangesFunc] = useState();
  const [showListDiscardModal, setShowListDiscardModal] = useState(false);

  // Subscribe to only the scalar fields we read, so this always-mounted sidebar button doesn't
  // re-render on unrelated changes to the filterList / filterListStatuses slices.
  const filterListName = useSelector((state) => selectFilterList(state)?.name);
  const canModify = useSelector((state) => selectFilterListStatuses(state)?.canModify);
  const filterListId = useSelector((state) => selectFilterListStatuses(state)?.id);

  const openCreatePatientPopover = useCallback(() => {
    setDestroyPopoverOnClose(false);
    setOpen(true);
    dispatch(ACTIONS.setFormPopups(false, null));
  }, [dispatch]);

  const handleCreatePatientClick = () => {
    // A saved list has unsaved changes in the background: confirm discard first so the create
    // flow never runs while the list is dirty (which otherwise surfaces a stray "unsaved" popup
    // on Manage patient details / Find assistance programs).
    // Gate on filterListName as well as canModify — Redux canModify can be left stale-true after
    // leaving a dirty list via a non-sidebar discard path, and without a loaded list the discard
    // modal (guarded below by filterListName) can't render, which would otherwise dead-end the button.
    if (canModify && filterListName) {
      setShowListDiscardModal(true);
      return;
    }
    openCreatePatientPopover();
  };

  const discardListChangesAndCreate = useCallback(() => {
    // Discard the unsaved list changes in place: marking the list unmodified and re-fetching it
    // re-applies its saved filters (see findSavedListById), so we stay on the list page with a
    // clean state instead of navigating away. Then open the create-patient flow.
    dispatch(ACTIONS.markSavedFilterAsChanged(filterListId, false));
    dispatch(ACTIONS.searchSavedListByIdAction(filterListId));
    dispatch(ACTIONS.updateFilterListStatuses(filterListId, false, false));
    setShowListDiscardModal(false);
    openCreatePatientPopover();
  }, [dispatch, filterListId, openCreatePatientPopover]);

  const handleListDiscardCancel = useCallback(() => setShowListDiscardModal(false), []);

  const isOmUser = user?.role === roles.OPERATION_MANGER;
  // useGetSetting returns "" when the key is absent or not yet loaded; !== false treats "" as enabled
  const canCreatePatient = isManualPatientCreationEnabled !== false || isOmUser;

  useEffect(() => {
    if (!discardChangesPopupVisible) {
      setDiscardChangesFunc(() => discardChanges);
    }
  }, [discardChangesPopupVisible]);

  const discardChanges = () => {
    setDiscardChangesPopupVisible(false);
    closePopover();
  };

  const defineDiscardFunc = (func) => {
    setDiscardChangesFunc(() => func);
  };

  const onClickCancel = () => {
    setDiscardChangesPopupVisible(false);
  };

  const setDiscardChangesPopupVisibility = (visibility) => {
    setDiscardChangesPopupVisible(visibility);
  };

  const closePopover = () => {
    setDestroyPopoverOnClose(true);
    setOpen(false);
  };

  if (!canCreatePatient) {
    return null;
  }

  return (
    <>
      <StyledModal
        id="create_new_patient_popover"
        open={open}
        destroyOnClose={destroyPopoverOnClose}
        zIndex={900}
        bodyStyle={{ borderRadius: 12, border: `1px solid ${GRAY_300}`, padding: 0 }}
        width={833}
        footer={null}
        closable={false}
        closeIcon={<CloseOutlined id="close_create_new_patient_popover" />}
        centered
      >
        <NewPatientForm
          closePopover={closePopover}
          setDiscardChangesPopupVisibility={setDiscardChangesPopupVisibility}
          defineDiscardFunc={defineDiscardFunc}
        />
      </StyledModal>
      {collapsed ? (
        <Tooltip placement="right" trigger="hover" title={t("new_patient.create_patient_tooltip")}>
          <PrimaryActionButton
            collapsed
            icon={<AddPatient />}
            id="add_new_patient_button"
            aria-label={t("new_patient.create_patient_tooltip")}
            onClick={handleCreatePatientClick}
          />
        </Tooltip>
      ) : (
        <PrimaryActionButton icon={<AddPatient />} id="add_new_patient_button" onClick={handleCreatePatientClick}>
          {t("new_patient.create_new_patient")}
        </PrimaryActionButton>
      )}

      <DiscardChangesPopup
        visible={discardChangesPopupVisible}
        onClickDiscard={() => discardChangesFunc()}
        onClickCancel={onClickCancel}
        popupTitle={t("managePatientDetails.cancel_modal_title")}
      />

      {filterListName && (
        <DiscardListChangesModal
          open={showListDiscardModal}
          onCancel={handleListDiscardCancel}
          onOk={discardListChangesAndCreate}
        />
      )}
    </>
  );
};

export default withRouter(AddNewPatientButton);
